/**
 * What one drop row sends a group to, read from the planner's preview
 * (user, 19/10/2026: "I can see it directly which list on the hour"): the
 * pinned list, the group's one list, every list of today's set (each drop
 * to each), or this drop's turn (alternate). The preview reflects the SAVED
 * choice — when the draft changed it, the row says so instead of guessing.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { DayPlanDropRow, DayPlanGroupRow } from '@trafficpulse/shared';

import { listChoiceOf } from '../../../../groups/lists-per-day/list-choice';
import type { GroupRef } from '../../../hooks/use-general-times';

export type GroupListsKind = 'pinned' | 'rank' | 'one' | 'each' | 'alternate';

export interface GroupListsView {
  kind: GroupListsKind;
  /** The list names this drop goes to, in order (empty = not known yet). */
  names: string[];
  /** The preview is from before an unsaved change of the group's choice. */
  stale: boolean;
  /** This drop is in today's plan (else the names are what it would go to). */
  planned: boolean;
  /** 19/10/2026 — the drop's rank of the group's lists of the day (kind 'rank'). */
  rank?: number;
}

interface Input {
  group: GroupRef;
  /** The row's planned drops (its own and its per-list ones). */
  planned: DayPlanDropRow[];
  /** The group as the preview planned it for this container. */
  planGroup?: DayPlanGroupRow;
  pinName: string | null;
  /** The group's name of its only list — what a drop not planned today still goes to. */
  onlyName?: string | null;
  /** The drop's rank for this group (only on all lists). */
  rank?: number;
}

const nameOf = (drop: DayPlanDropRow) => drop.listName ?? `List #${drop.listWiserId}`;

export function groupListsView({ group, planned, planGroup, pinName, onlyName = null, rank }: Input): GroupListsView {
  if (pinName !== null) return { kind: 'pinned', names: [pinName], stale: false, planned: true };
  const mine = planned.filter((d) => d.groupId === group.id).map(nameOf);
  const today = (planGroup?.listsToday ?? []).map((l) => l.name ?? `List #${l.wiserId}`);
  if (rank !== undefined && listChoiceOf(group.listsPerDay) === 'all') return rankView(rank, mine, planGroup);
  const kind: GroupListsKind = listChoiceOf(group.listsPerDay) === 'one' ? 'one' : group.allListsMode === 'ALTERNATE' ? 'alternate' : 'each';
  const stale = kind === 'one' ? today.length > 0 : !onlyName && (today.length === 0 || (kind === 'alternate' && mine.length > 1));
  // A drop not planned today still shows the list(s) it would go to (user, 19/10/2026: "why some say best list of the day?").
  const ofDay = planGroup?.listOfDay ? [planGroup.listOfDay.name ?? `List #${planGroup.listOfDay.wiserId}`] : [];
  const only = onlyName ? [onlyName] : [];
  const fallback = kind === 'one' ? (ofDay.length ? ofDay : only) : kind === 'each' && today.length ? today : only;
  return { kind, names: mine.length ? mine : fallback, stale, planned: mine.length > 0 };
}

/** A rank drop names today's #rank — the engine's own set (the row's saved drop may be from another rank or
 *  another choice). "Save to see" only while the saved choice is not all lists yet (no set in the plan). */
function rankView(rank: number, mine: string[], planGroup: DayPlanGroupRow | undefined): GroupListsView {
  if (planGroup?.listsToday === undefined) return { kind: 'rank', names: [], stale: true, planned: false, rank };
  const today = planGroup.listsToday.map((l) => l.name ?? `List #${l.wiserId}`);
  const name = today[rank - 1];
  return { kind: 'rank', names: name ? [name] : [], stale: false, planned: name !== undefined && mine.includes(name), rank };
}

/** The cell's words when the preview has no answer for this row. */
export function emptyListsWords(view: GroupListsView, listCount: number | null): string {
  if (view.stale) return view.kind === 'rank' ? `#${view.rank} — save to see its list` : 'Save to see its list';
  if (view.kind === 'rank') return `#${view.rank} — no list today`;
  if (view.kind === 'each') return listCount && listCount > 1 ? `All ${listCount} lists` : 'All lists';
  if (view.kind === 'alternate') return 'Not planned today';
  return listCount === 1 ? 'Its only list' : 'Best list of the day';
}
